<template>
  <!-- Modal Backdrop -->
  <div
    v-if="isOpen"
    class="fixed inset-0 z-[70] overflow-y-auto"
    aria-labelledby="modal-title"
    role="dialog"
    aria-modal="true"
  >
    <!-- Background overlay -->
    <div class="flex items-end sm:items-center justify-center min-h-screen p-0 sm:p-4">
      <div 
        class="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        aria-hidden="true"
        @click="() => closeModal()"
      ></div>

      <!-- Modal panel - Mobile optimized -->
      <div 
        class="relative bg-white rounded-t-xl sm:rounded-lg shadow-xl w-full max-w-md mx-auto transform transition-all max-h-[95vh] sm:max-h-[90vh] flex flex-col"
        @click.stop
      >
        <!-- Close button -->
        <div class="absolute top-0 right-0 pt-4 pr-4 z-10">
          <button
            type="button"
            @click="() => closeModal()"
            class="bg-white rounded-full p-2 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-lg"
          >
            <span class="sr-only">Close</span>
            <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Modal content -->
        <div class="flex-1 overflow-y-auto p-6 sm:p-8 pb-8 sm:pb-6">
          <!-- Login Form -->
          <LoginForm
            v-if="currentMode === 'login'"
            @switch-mode="switchMode"
            @login-success="handleAuthSuccess"
          />

          <!-- Register Form -->
          <RegisterForm
            v-else-if="currentMode === 'register'"
            @switch-mode="switchMode"
            @register-success="handleAuthSuccess"
          />

          <!-- Forgot Password Form -->
          <ForgotPasswordForm
            v-else-if="currentMode === 'forgot-password'"
            @switch-mode="switchMode"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/store/auth'
import LoginForm from './LoginForm.vue'
import RegisterForm from './RegisterForm.vue'
import ForgotPasswordForm from './ForgotPasswordForm.vue'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()

// Props
const props = defineProps({
  initialMode: {
    type: String,
    default: 'login',
    validator: (value) => ['login', 'register', 'forgot-password'].includes(value)
  },
  show: {
    type: Boolean,
    default: false
  },
  hasPendingAction: {
    type: Boolean,
    default: false
  }
})

// Emits
const emit = defineEmits(['close', 'auth-success'])

// State
const currentMode = ref(props.initialMode)
const isOpen = ref(props.show)

// Computed
const modalTitle = computed(() => {
  switch (currentMode.value) {
    case 'login':
      return 'Sign in to your account'
    case 'register':
      return 'Create your account'
    case 'forgot-password':
      return 'Reset your password'
    default:
      return 'Authentication'
  }
})

// Watch for route changes to determine modal mode
watch(() => route.meta.authModal, (newMode) => {
  if (newMode) {
    currentMode.value = newMode
    isOpen.value = true
  }
}, { immediate: true })

// Watch for prop changes
watch(() => props.show, (newValue) => {
  isOpen.value = newValue
})

watch(() => props.initialMode, (newMode) => {
  currentMode.value = newMode
})

// Methods
const switchMode = (mode) => {
  authStore.clearError()
  currentMode.value = mode

  // Update URL without navigation
  const routeName = mode === 'login' ? 'Login' :
                   mode === 'register' ? 'Register' : 'ForgotPassword'

  router.replace({
    name: routeName,
    query: route.query // Preserve query params like redirect
  })
}

const closeModal = (isSuccess = false) => {
  if (import.meta.env.DEV) console.log('closeModal called in AuthModal', {
    isAuthRoute: !!route.meta.authModal,
    currentPath: route.path,
    isSuccess,
    hasPendingAction: props.hasPendingAction
  });
  isOpen.value = false
  emit('close')

  // Handle redirection on success
  if (isSuccess) {
    // If there's a pending action (like trending card search), let App.vue handle navigation
    if (props.hasPendingAction) {
      if (import.meta.env.DEV) console.log('Pending action exists, letting App.vue handle navigation');
      return;
    }

    const rawRedirect = route.query.redirect
    const redirectTo = typeof rawRedirect === 'string' && rawRedirect.startsWith('/') ? rawRedirect : null

    if (redirectTo) {
      // Came from a protected route — go there
      if (import.meta.env.DEV) console.log(`AuthModal success redirection to: ${redirectTo}`);
      setTimeout(() => {
        router.push(redirectTo)
          .catch(err => {
            console.error('AuthModal success navigation failed:', err);
            router.push('/');
          });
      }, 100);
    } else if (route.meta.authModal) {
      // On a dedicated /login page with no redirect target — go home
      if (import.meta.env.DEV) console.log('AuthModal success: on auth page, redirecting to home');
      setTimeout(() => router.push('/'), 100);
    }
    // Overlay modal with no redirect — just close, user stays on current page
    return;
  }

  // Handle "cancel/away" navigation for dedicated auth routes.
  // Use ?returnTo= (where the user came FROM) if available — e.g. /search-results.
  // Never use ?redirect= on cancel: that's the protected destination and would
  // bounce the user straight back into the login loop.
  if (route.meta.authModal) {
    const rawReturnTo = route.query.returnTo
    const returnTo = typeof rawReturnTo === 'string' && rawReturnTo.startsWith('/') ? rawReturnTo : '/'
    router.push(returnTo)
      .catch(err => console.error('AuthModal cancel navigation failed:', err));
  }
}

const handleAuthSuccess = () => {
  if (import.meta.env.DEV) console.log('handleAuthSuccess called in AuthModal');
  emit('auth-success')
  closeModal(true) // Pass true to trigger success redirection
}

// Handle escape key
const handleEscape = (event) => {
  if (event.key === 'Escape' && isOpen.value) {
    closeModal()
  }
}

// Prevent body scroll when modal is open
const toggleBodyScroll = (disable) => {
  if (typeof document !== 'undefined') {
    if (disable) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
  }
}

watch(isOpen, (newValue) => {
  toggleBodyScroll(newValue)
})

onMounted(() => {
  document.addEventListener('keydown', handleEscape)
  
  // Check if we should open the modal based on route
  if (route.meta.authModal) {
    isOpen.value = true
  }
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleEscape)
  toggleBodyScroll(false) // Ensure body scroll is restored
})
</script>

<style scoped>
/* Smooth transitions for modal */
.modal-enter-active, .modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from, .modal-leave-to {
  opacity: 0;
}

.modal-content-enter-active, .modal-content-leave-active {
  transition: all 0.3s ease;
}

.modal-content-enter-from, .modal-content-leave-to {
  opacity: 0;
  transform: translate3d(0, -50px, 0);
}
</style>